import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Toast from '../actions/Toasts';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import apiService from '../../services/apiServices';
import { extraHoursPay, MONTHLY_HOURS } from '../../utils/salaryCalc';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert, Box, Chip, Divider, InputAdornment,
  Stack, TextField, Typography,
} from '@mui/material';

const baseHourly = (salary) => (parseFloat(salary) || 0) / MONTHLY_HOURS;
const fmt2       = (n) => (parseFloat(n) || 0).toFixed(2);
const toNum      = (v) => parseFloat(v) || 0;

/**
 * Gjashtë kategoritë e LP Nenit 56, në një listë të vetme.
 *
 * `premiumOnly` e ndan familjen: nata, kujdestaria dhe festa e punuar në ditë
 * PUNE janë orë që paga mujore i mbulon tashmë — atyre u shtohet vetëm
 * përqindja. Të tjerat janë orë jashtë pagës: orë e plotë plus shtesa.
 *
 * Më parë ekzistonin vetëm tri të parat; nata dhe kujdestaria shpalleshin te
 * koka e dialogut dhe nuk paguheshin dot askund.
 */
const CATEGORIES = [
  { key: 'ExtraHours',              rateKey: 'overtime',      pctKey: 'rOvertime', color: '#3E8FA3', premiumOnly: false, labelKey: 'salaries.overtimeWork',   hoursKey: 'salaries.overtimeHours' },
  { key: 'ExtraHoursWeekend',       rateKey: 'rest',          pctKey: 'rWeekend',  color: '#D97706', premiumOnly: false, labelKey: 'salaries.weekendWork',    hoursKey: 'salaries.weekendHours' },
  { key: 'ExtraHoursHoliday',       rateKey: 'holiday',       pctKey: 'rHoliday',  color: '#DC2626', premiumOnly: false, labelKey: 'salaries.holidayWork',    hoursKey: 'salaries.holidayHours' },
  { key: 'ExtraHoursNight',         rateKey: 'night',         pctKey: 'rNight',    color: '#4F46E5', premiumOnly: true,  labelKey: 'salaries.nightWorkTitle', hoursKey: 'salaries.nightHours' },
  { key: 'ExtraHoursHolidayWorked', rateKey: 'holidayWorked', pctKey: 'rHoliday',  color: '#B91C1C', premiumOnly: true,  labelKey: 'salaries.holidayWorkedTitle', hoursKey: 'salaries.holidayWorkedHours' },
  { key: 'ExtraHoursOnCall',        rateKey: 'onCall',        pctKey: 'rOnCall',   color: '#0369A1', premiumOnly: true,  labelKey: 'salaries.onCallTitle',    hoursKey: 'salaries.onCallHours' },
];

// ─── Ora e edituar ─────────────────────────────────────────────────────────────
function HoursField({ label, value, onChange }) {
  return (
    <TextField
      size="small"
      fullWidth
      type="number"
      label={label}
      value={value === '' ? '' : value}
      onChange={e => onChange(e.target.value === '' ? '' : e.target.value)}
      InputLabelProps={{ shrink: true }}
      InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
      inputProps={{ min: 0, step: 0.5 }}
    />
  );
}

// ─── Çmimi/orë (vetëm lexim — nga paga) ──────────────────────────────────────
function RateField({ label, rate, pct, color, premiumOnly }) {
  const { t } = useTranslation();
  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={fmt2(rate)}
      disabled
      InputLabelProps={{ shrink: true }}
      InputProps={{ endAdornment: <InputAdornment position="end">€/h</InputAdornment> }}
      helperText={
        <span style={{ color }}>
          {t(premiumOnly ? 'salaries.rateFormulaPremium' : 'salaries.rateFormula', { pct, hours: MONTHLY_HOURS })}
        </span>
      }
      sx={{
        '& .MuiInputBase-input.Mui-disabled': { color: color || '#374151', WebkitTextFillColor: color || '#374151', fontWeight: 700 },
        '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: (color || '#6B7280') + '50' },
      }}
    />
  );
}

// ─── Total field ─────────────────────────────────────────────────────────────
function TotalField({ hours, rate }) {
  const { t } = useTranslation();
  const total = toNum(hours) * rate;
  return (
    <TextField
      size="small"
      fullWidth
      label={t('salaries.totalValue')}
      value={fmt2(total)}
      disabled
      InputLabelProps={{ shrink: true }}
      InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
      sx={{ '& .MuiInputBase-input.Mui-disabled': { fontWeight: 700, WebkitTextFillColor: '#111827' } }}
    />
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function ExtraHours(props) {
  const { t } = useTranslation();
  const [toast,   setToast]  = useState({ open: false, message: '', variant: '' });
  const [contract, setContract] = useState(null);

  // Orët — vetëm këto i fut përdoruesi; çmimet dalin nga paga dhe politika.
  const [hours, setHours] = useState({});

  const loadedForId = useRef(null);

  // ─── normat fiskale ────────────────────────────────────────────────────────
  const fiscalRates = props.fiscalRates || null;
  const baseSalary  = toNum(props.baseSalary ?? 0);

  // Një formulë e vetme për dialogun dhe për pagën: `extraHoursPay`.
  const pay = extraHoursPay(hours, baseSalary, fiscalRates || {});
  const { rates: pct, hourRates } = pay;

  const hasFiscal = !!fiscalRates && baseSalary > 0;

  // ─── ngarko orët nga item kur hapet dialogi ────────────────────────────────
  useEffect(() => {
    if (!props.open) return;
    const next = {};
    CATEGORIES.forEach((c) => { next[c.key] = toNum(props.item?.[c.key]) || ''; });
    setHours(next);
  }, [props.open, props.item]);

  // ─── ngarko kontratën njëherë ──────────────────────────────────────────────
  useEffect(() => {
    if (!props.open || !props.id || loadedForId.current === props.id) return;
    loadedForId.current = props.id;
    apiService()
      .query('getWorkerContracts', 'id=' + props.id)
      .then(wc => {
        if (wc.success && wc.data?.length) {
          setContract(wc.data[wc.data.length - 1]);
        }
      });
  }, [props.open, props.id]);

  // ─── njofto prindërin çdo herë që ndryshojnë orët ────────────────────────
  useEffect(() => {
    if (!props.open) return;
    const payload = {};
    CATEGORIES.forEach((c) => { payload[c.key] = toNum(hours[c.key]); });
    props.setHours(payload, props.no, pay.total);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, pay.total]);

  // ─── ruaj normat në kontratë ───────────────────────────────────────────────
  const save = () => {
    if (!contract) return;
    const updated = {
      ...contract,
      HourSalary:        hourRates.overtime,
      HourSalaryWeekend: hourRates.rest,
      HourSalaryHoliday: hourRates.holiday,
    };
    apiService().put('workerContract', updated).then(r => {
      setToast({ open: true, message: r.success ? t('salaries.ratesSaved') : t('common.errorOccurred'), variant: r.success ? 'success' : 'error' });
    });
  };

  const handleClose = () => {
    loadedForId.current = null;
    props.handleClose(false);
  };

  // ─── totalet ──────────────────────────────────────────────────────────────
  const totalHours = CATEGORIES.reduce((a, c) => a + toNum(hours[c.key]), 0);
  const totalPay   = pay.total;

  return (
    <div>
      <Toast {...toast} />
      <Dialog open={Boolean(props.open)} onClose={handleClose} fullWidth maxWidth="md">

        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>{props.tittle || t('salaries.extraHours')}</Typography>
        </DialogTitle>

        <DialogContent>

          {/* ── Paneli i informacionit nga politika fiskale ── */}
          {hasFiscal ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{fiscalRates?.Name || 'Politika fiskale aktive'} — LP Neni 56:</strong>
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {CATEGORIES.map((c) => (
                  <Box key={c.key}>
                    <Typography variant="caption" color="text.secondary">{t(c.labelKey, { pct: pct[c.pctKey] }).replace(/\s*\(.*\)\s*$/, '')}</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: c.color }}>
                      +{pct[c.pctKey]}%
                      <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 4 }}>
                        = {fmt2(hourRates[c.rateKey])}€/h
                      </span>
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('salaries.baseSalaryFormula', { salary: baseSalary, hours: MONTHLY_HOURS, rate: fmt2(baseHourly(baseSalary)) })}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('salaries.baseSalaryOrPolicyNotFound')}
            </Alert>
          )}

          {/* Nga vjen shifra: orët mbushen nga prezenca me butonin «Nga Orari»
              te lista; këtu korrigjohen. Pa këtë rresht, dialogu dukej si i
              vetmi burim dhe askush s'e dinte se ekziston mbushja automatike. */}
          <Alert severity="success" variant="outlined" sx={{ mb: 2, py: 0.25 }}>
            <Typography variant="caption">{t('salaries.hoursFromAttendance')}</Typography>
          </Alert>

          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                {CATEGORIES.map((c, i) => (
                  <React.Fragment key={c.key}>
                    {i > 0 && <Grid item xs={12}><Divider /></Grid>}
                    <Grid item xs={12}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.color, flexShrink: 0 }} />
                        <Typography variant="caption" fontWeight={700} sx={{ color: c.color }}>
                          {t(c.labelKey, { pct: pct[c.pctKey] })}
                        </Typography>
                        {/* Familja e orës thuhet aty ku lexohet çmimi, jo në një shënim poshtë. */}
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t(c.premiumOnly ? 'salaries.premiumOnly' : 'salaries.fullHourPlus')}
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={4}>
                      <HoursField
                        label={t(c.hoursKey)}
                        value={hours[c.key] ?? ''}
                        onChange={(v) => setHours((prev) => ({ ...prev, [c.key]: v }))}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <RateField
                        label={t('salaries.ratePerHour')}
                        rate={hourRates[c.rateKey]}
                        pct={pct[c.pctKey]}
                        color={c.color}
                        premiumOnly={c.premiumOnly}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TotalField hours={hours[c.key]} rate={hourRates[c.rateKey]} />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* ── Totali ── */}
          {totalHours > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#EEF2FF', borderRadius: 2, border: '1px solid #C7D2FE' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={`${t('salaries.total')}: ${fmt2(totalHours)}h`} color="primary" variant="outlined" />
                  {CATEGORIES.filter((c) => toNum(hours[c.key]) > 0).map((c) => (
                    <Chip key={c.key} size="small" variant="outlined" sx={{ fontSize: 10, borderColor: c.color, color: c.color }}
                      label={`${toNum(hours[c.key])}h × ${fmt2(hourRates[c.rateKey])}€ = ${fmt2(toNum(hours[c.key]) * hourRates[c.rateKey])}€`} />
                  ))}
                </Stack>
                <Typography variant="h6" fontWeight={800} color="primary.main">
                  {t('salaries.total')}: {fmt2(totalPay)}€
                </Typography>
              </Stack>
            </Box>
          )}

        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>{t('common.close')}</Button>
          <Button onClick={save} startIcon={<SaveIcon />} variant="contained" sx={{ textTransform: 'none' }}>
            {t('salaries.saveRates')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
